/* eslint-disable max-len */
import React from 'react';
import { number, object, oneOfType, string } from 'prop-types';
import _find from 'lodash/find';
import { useDroppable } from '@dnd-kit/core';
import { Tooltip, useBreakpoint } from '@one-thd/sui-atomic-components';
import {
  useProductDataStore,
  useDigitalDataStore,
  usePromoStore,
} from '../../../store';
import { useDesignStore } from '../../../store/design/context';

import { useClickAway } from '../../../hooks/useClickAway';

const TrashIconOpen = () => (
  <svg width="40" height="61" viewBox="0 0 40 61" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.6001 17.6948L34.956 13.6364L39.7946 53.7066C39.9932 55.3515 38.8209 56.8472 37.1762 57.0473L9.77642 60.381C8.1317 60.5811 6.63736 59.4099 6.43873 57.765L1.6001 17.6948Z" fill="#FFFFFF" />
    <rect width="40.0075" height="3.20383" transform="matrix(0.985393 0.170296 -0.168699 0.985668 0.540527 2.32422)" fill="#FFFFFF" />
    <rect width="11.2047" height="4.81238" transform="matrix(0.985358 0.170502 -0.168503 0.985702 15.5449 0.0256348)" fill="#FFFFFF" />
  </svg>
);

const TrashIconClosed = React.forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <div ref={ref} {...props}>
    <svg xmlns="http://www.w3.org/2000/svg" width="37" height="48" viewBox="0 0 37 48" fill="none">
      <mask id="path-1-inside-1_1688_138150" fill="white">
        <path fillRule="evenodd" clipRule="evenodd" d="M28 0H9V3H0V6H37V3H28V0Z" />
      </mask>
      <path fillRule="evenodd" clipRule="evenodd" d="M28 0H9V3H0V6H37V3H28V0Z" fill="#F96302" />
      <path d="M9 0V-1H8V0H9ZM28 0H29V-1H28V0ZM9 3V4H10V3H9ZM0 3V2H-1V3H0ZM0 6H-1V7H0V6ZM37 6V7H38V6H37ZM37 3H38V2H37V3ZM28 3H27V4H28V3ZM9 1H28V-1H9V1ZM10 3V0H8V3H10ZM0 4H9V2H0V4ZM1 6V3H-1V6H1ZM37 5H0V7H37V5ZM36 3V6H38V3H36ZM28 4H37V2H28V4ZM27 0V3H29V0H27Z" fill="white" mask="url(#path-1-inside-1_1688_138150)" />
      <path d="M3.5 8.5H33.5V45C33.5 46.3807 32.3807 47.5 31 47.5H6C4.61929 47.5 3.5 46.3807 3.5 45V8.5Z" fill="#F96302" stroke="white" />
    </svg>
  </div>
));

const removeProductFromDesign = (stackItemId, bundleId, removeProduct) => {
  if (bundleId) {
    return removeProduct(stackItemId, bundleId);
  }

  return removeProduct(stackItemId);
};

const Trash = (props) => {
  const { id, children } = props;
  const { setNodeRef, isOver } = useDroppable({
    id: 'trash-can',
    data: {
      ...props,
    }
  });

  const matches = useBreakpoint('lg');
  const isDesktop = matches.greater;

  const design = useDesignStore((state) => state.design);
  const tooltipText = isDesktop ? 'Drag items here to remove' : 'Tap an item in your stack to highlight it, then tap here to remove it.';

  const isVisualizerDrag = design?.dragEvent?.isVisualizerDrag;
  const isDragging = design?.dragEvent?.isDragging;
  const activateIcon = (isVisualizerDrag && isDragging) || design?.selectedVisualizerItemStackId;
  const storeProductData = useProductDataStore((state) => state.productData);
  const removeProduct = useDesignStore((state) => state.remove);
  const trackRemoveItemsFromStackEvent = useDigitalDataStore((state) => state.trackRemoveItemsFromStackEvent);
  const selectVisualizerItem = useDesignStore((state) => state.selectVisualizerItem);
  const resetDiscounts = usePromoStore((state) => state.resetDiscounts);
  const ref = useClickAway(() => {
    selectVisualizerItem(null);
  });

  const handleItemRemove = () => {
    if (design?.selectedVisualizerItemStackId) {
      const { Sku: sku, choiceBundle = null } = _find(
        design?.products,
        (product) => product.stackItemId === design.selectedVisualizerItemStackId
      );
      removeProductFromDesign(design.selectedVisualizerItemStackId, choiceBundle?.id, removeProduct);
      selectVisualizerItem(null);
      try {
        const { value: basePrice } = storeProductData?.[sku]?.pricing;
        const productArray = [{ sku, basePrice }];
        trackRemoveItemsFromStackEvent('trash can button', productArray);
      } catch (error) {
        console.warn(`Failed to dispatch analytics: ${error.message}`);
      }
      if (design?.products.length === 1) {
        resetDiscounts();
      }
    }
  };

  const style = {
    zIndex: 1,
    position: 'absolute',
    bottom: 0,
    right: 0,
    transition: 'all .2s ease-in-out',
    color: 'var(--swiper-theme-color)',
    backgroundColor: 'transparent',
    ...(activateIcon && {
      color: '#fff',
      padding: '10px 0px 0px 10px',
      backgroundColor: '#FFC206',
      borderTopLeftRadius: '100%',
    }),
    ...(isOver && {
      backgroundColor: 'red',
    })
  };

  return (
    <div
      onClick={handleItemRemove}
      role="button"
      tabIndex={0}
      ref={setNodeRef}
      style={style}
    >
      {activateIcon
        ? (
          <div
            ref={ref}
            className="trash-can-wrapper"
            style={{
              bottom: 0,
              right: 0,
              margin: '1rem',
              ...(activateIcon && {
                padding: '30px 0px 0px 30px',
              }),
            }}
          >
            <TrashIconOpen />
          </div>
        )
        : (
          <div
            role="button"
            tabIndex={0}
            className="trash-can-wrapper"
            aria-label="trash"
            style={{
              bottom: 0,
              right: 0,
              margin: '1rem',
              ...(activateIcon && {
                padding: '30px 0px 0px 30px',
              }),
            }}
          >
            <Tooltip
              placement="top-end"
              offset="snug"
              title={tooltipText}
            >
              <TrashIconClosed />
            </Tooltip>
          </div>
        )}
    </div>
  );
};

Trash.propTypes = {
  id: oneOfType([number, string]),
  // eslint-disable-next-line react/forbid-prop-types
  children: object,
};

Trash.defaultProps = {
  id: null,
  children: {},
};

export default Trash;
